/** Nhóm kênh bán: Offline / Online */
export const NHOM_KENH_BAN = {
  BAN_OFF: 'BAN_OFF',
  BAN_ON: 'BAN_ON',
} as const

export type NhomKenhBanValue = (typeof NHOM_KENH_BAN)[keyof typeof NHOM_KENH_BAN]

/**
 * Kênh bán (1 field `kenhBan` trên chứng từ).
 * - BAN_OFF: TU_GIAO | VAN_CHUYEN_THU_3
 * - BAN_ON: chỉ SPE (Shopee) + TTS (TikTok Shop)
 * Giá trị Lazada / FB-Zalo / Website giữ trong enum để đọc HĐ cũ.
 */
export const KENH_BAN = {
  TU_GIAO: 'TU_GIAO',
  VAN_CHUYEN_THU_3: 'VAN_CHUYEN_THU_3',
  SHOPEE: 'SHOPEE',
  TIKTOK_SHOP: 'TIKTOK_SHOP',
  LAZADA: 'LAZADA',
  FACEBOOK_ZALO: 'FACEBOOK_ZALO',
  WEBSITE: 'WEBSITE',
} as const

export type KenhBanValue = (typeof KENH_BAN)[keyof typeof KENH_BAN]

export const KENH_BAN_OFF: KenhBanValue[] = [KENH_BAN.TU_GIAO, KENH_BAN.VAN_CHUYEN_THU_3]

/** Bán online đang hỗ trợ: SPE + TTS. */
export const KENH_BAN_ON: KenhBanValue[] = [KENH_BAN.SHOPEE, KENH_BAN.TIKTOK_SHOP]

/** Kênh online cũ — không hiện filter/select mới, vẫn normalize/hiển thị. */
export const KENH_BAN_ON_CU: KenhBanValue[] = [
  KENH_BAN.LAZADA,
  KENH_BAN.FACEBOOK_ZALO,
  KENH_BAN.WEBSITE,
]

export const KENH_BAN_THEO_NHOM: Record<NhomKenhBanValue, KenhBanValue[]> = {
  BAN_OFF: KENH_BAN_OFF,
  BAN_ON: KENH_BAN_ON,
}

export const DANH_SACH_KENH_BAN: KenhBanValue[] = [...KENH_BAN_OFF, ...KENH_BAN_ON]

const DANH_SACH_KENH_BAN_DOC: readonly string[] = [
  ...DANH_SACH_KENH_BAN,
  ...KENH_BAN_ON_CU,
]

/** @deprecated Dùng DANH_SACH_KENH_BAN */
export const KENH_BAN_POS_OPTIONS: KenhBanValue[] = DANH_SACH_KENH_BAN

export const NHOM_KENH_BAN_LABELS_VI: Record<NhomKenhBanValue, string> = {
  BAN_OFF: 'Bán offline',
  BAN_ON: 'Bán online',
}

export const KENH_BAN_LABELS_VI: Record<KenhBanValue, string> = {
  TU_GIAO: 'Tự giao',
  VAN_CHUYEN_THU_3: 'VC thứ 3',
  SHOPEE: 'Shopee',
  TIKTOK_SHOP: 'TikTok',
  LAZADA: 'Lazada',
  FACEBOOK_ZALO: 'Facebook / Zalo',
  WEBSITE: 'Website',
}

/** Label select: Off/On · chi tiết */
export const KENH_BAN_LABELS_SELECT_VI: Record<KenhBanValue, string> = {
  TU_GIAO: 'Off · Tự giao',
  VAN_CHUYEN_THU_3: 'Off · VC thứ 3',
  SHOPEE: 'On · Shopee',
  TIKTOK_SHOP: 'On · TikTok',
  LAZADA: 'On · Lazada',
  FACEBOOK_ZALO: 'On · Facebook / Zalo',
  WEBSITE: 'On · Website',
}

export const KENH_BAN_NHOM: Record<KenhBanValue, NhomKenhBanValue> = {
  TU_GIAO: NHOM_KENH_BAN.BAN_OFF,
  VAN_CHUYEN_THU_3: NHOM_KENH_BAN.BAN_OFF,
  SHOPEE: NHOM_KENH_BAN.BAN_ON,
  TIKTOK_SHOP: NHOM_KENH_BAN.BAN_ON,
  LAZADA: NHOM_KENH_BAN.BAN_ON,
  FACEBOOK_ZALO: NHOM_KENH_BAN.BAN_ON,
  WEBSITE: NHOM_KENH_BAN.BAN_ON,
}

export const KENH_BAN_OPTIONS = DANH_SACH_KENH_BAN.map((value) => ({
  value,
  labelVi: KENH_BAN_LABELS_SELECT_VI[value],
  nhom: KENH_BAN_NHOM[value],
}))

/** Nhóm option cho filter / select có header */
export const KENH_BAN_GROUPED_OPTIONS: Array<{
  nhom: NhomKenhBanValue
  labelVi: string
  options: Array<{ value: KenhBanValue; labelVi: string }>
}> = [
  {
    nhom: NHOM_KENH_BAN.BAN_OFF,
    labelVi: NHOM_KENH_BAN_LABELS_VI.BAN_OFF,
    options: KENH_BAN_OFF.map((value) => ({
      value,
      labelVi: KENH_BAN_LABELS_VI[value],
    })),
  },
  {
    nhom: NHOM_KENH_BAN.BAN_ON,
    labelVi: NHOM_KENH_BAN_LABELS_VI.BAN_ON,
    options: KENH_BAN_ON.map((value) => ({
      value,
      labelVi: KENH_BAN_LABELS_VI[value],
    })),
  },
]

/** Map giá trị cũ → kênh mới */
const LEGACY_KENH_BAN: Record<string, KenhBanValue> = {
  BAN_TRUC_TIEP: KENH_BAN.TU_GIAO,
  KHAC: KENH_BAN.TU_GIAO,
}

export function normalizeKenhBan(raw: unknown): KenhBanValue {
  const s = typeof raw === 'string' ? raw.trim() : ''
  if (!s) return KENH_BAN.TU_GIAO
  if (DANH_SACH_KENH_BAN_DOC.includes(s)) {
    return s as KenhBanValue
  }
  return LEGACY_KENH_BAN[s] ?? KENH_BAN.TU_GIAO
}

/** Lọc list HĐ: loại đơn bán online (cờ `banOnline` / `banOnlineGop`). */
export const LOAI_BAN_ONLINE_LOC = {
  ONLINE: 'ONLINE',
  BAN_GOP: 'BAN_GOP',
} as const

export type LoaiBanOnlineLocValue =
  (typeof LOAI_BAN_ONLINE_LOC)[keyof typeof LOAI_BAN_ONLINE_LOC]

export const LOAI_BAN_ONLINE_LOC_OPTIONS: LoaiBanOnlineLocValue[] = [
  LOAI_BAN_ONLINE_LOC.ONLINE,
  LOAI_BAN_ONLINE_LOC.BAN_GOP,
]

export function nhomOfKenhBan(raw: unknown): NhomKenhBanValue {
  return KENH_BAN_NHOM[normalizeKenhBan(raw)]
}
